import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Paper,
  Divider,
  Box,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  Chip,
  ButtonGroup,
  Tooltip,
  IconButton,
  Autocomplete,
  TextField,
  TableFooter
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonSearchIcon from '@mui/icons-material/PersonSearch'; // Cari arama için ikon
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import ReceiptIcon from '@mui/icons-material/Receipt'; 
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { tr } from 'date-fns/locale';
import { format, parseISO, isValid } from 'date-fns';
import { utils, writeFile } from 'xlsx';
import { accountingApi } from '../../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface AccountReportTabProps {
  companyId: string | null;
  screenId: string | null;
}

interface AccountTransaction {
  id: string;
  accountId: string;
  accountName: string;
  transactionType: string; // QUICK_SALE, payment_received vb.
  operationType?: string; // payment_received, payment_made
  description: string;
  amount: number;
  date: string; // YYYY-MM-DD
  createdAt: string; // ISO Timestamp
  referenceId?: string; // Fatura ID vb.
  referenceType?: string; // salesinvoice vb.
  currency: string;
  paymentMethod?: string;
  screenId?: string; // Bu alan usePayment'tan eklenmiş olmalı
  debitAmount?: number;
  creditAmount?: number;
  previousBalance?: number;
  nextBalance?: number;
  createdBy?: string; // Kasiyer bilgisi
}

// Tarih ve Saati Formatlama (String manipülasyonu ile - timezone sorunu yok)
const formatDateTime = (isoDateString: string): string => {
  if (!isoDateString) return '-';
  try {
    // ISO formatında bir tarih mi kontrol et (T içeriyor mu?)
    if (isoDateString.includes('T')) {
      // "2025-11-27T09:18:27.3824304" formatını parçalara ayır
      const [tarihKismi, saatKismi] = isoDateString.split('T');
      
      // Tarih kısmını Türkçe formatına çevir (yyyy-MM-dd -> dd.MM.yyyy)
      const [yil, ay, gun] = tarihKismi.split('-');
      const formatliTarih = `${gun}.${ay}.${yil}`;
      
      // Saat kısmını milisaniye olmadan al (09:18:27.3824304 -> 09:18)
      const saatParts = saatKismi.split(':');
      const formatliSaat = `${saatParts[0]}:${saatParts[1]}`; // Sadece saat:dakika
      
      return `${formatliTarih} ${formatliSaat}`;
    }
    
    // ISO formatında değilse normal tarih dönüşümü
    const date = new Date(isoDateString);
    return isValid(date) ? format(date, 'dd.MM.yyyy HH:mm', { locale: tr }) : '-';
  } catch (error) { 
    return '-'; 
  }
};

// Para birimi formatla
const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency', currency: currencyCode, minimumFractionDigits: 2
  }).format(amount);
};

// İşlem tipi için ikon ve etiket
const getTransactionTypeInfo = (transaction: AccountTransaction, t: (key: string) => string) => {
  const transType = transaction.transactionType?.toUpperCase();
  const opType = transaction.operationType?.toLowerCase();

  if (transType === 'QUICK_SALE') {
    return { label: t('accountReportTab.transactionTypes.quickSale'), icon: <ReceiptIcon fontSize="small" color="info" />, color: 'info' as 'info' };
  }
  // ✅ COLLECTION da Hızlı Satış olarak göster (POS ödemelerinde COLLECTION kullanılıyor)
  if (transType === 'COLLECTION' && transaction.paymentMethod === 'card_payment') {
    return { label: t('accountReportTab.transactionTypes.quickSale'), icon: <ReceiptIcon fontSize="small" color="info" />, color: 'info' as 'info' };
  }
  if (transType === 'COLLECTION' || opType === 'payment_received' || opType === 'payment_received_on_account' || opType === 'deposit') {
    return { label: t('accountReportTab.transactionTypes.collectionIncome'), icon: <AttachMoneyIcon fontSize="small" color="success" />, color: 'success' as 'success' };
  }
  if (opType === 'payment_made' || opType === 'payment_sent_on_account' || opType === 'withdrawal' || opType === 'expense') {
    return { label: t('accountReportTab.transactionTypes.paymentExpense'), icon: <MoneyOffIcon fontSize="small" color="error" />, color: 'error' as 'error' };
  }
  return { label: transaction.transactionType || t('accountReportTab.common.other'), icon: <ReceiptIcon fontSize="small" />, color: 'default' as 'default' };
};

const AccountReportTab = ({ companyId, screenId }: AccountReportTabProps): React.ReactElement => {
  const { t } = useTranslation('salesSystem');
  const [accountTransactions, setAccountTransactions] = useState<AccountTransaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [customerNameFilter, setCustomerNameFilter] = useState<string>('');
  const [selectedCashier, setSelectedCashier] = useState<string | null>(null);

  const reportTitle = t('accountReportTab.title');

  const fetchAccountTransactions = useCallback(async () => {
    if (!companyId || !screenId) {
      setAccountTransactions([]);
      setFilteredTransactions([]);
      setSelectedCashier(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log('📋 [CARİ RAPORU] SQL\'den cari hareketleri yükleniyor...', { companyId, screenId });
      
      const response = await accountingApi.get(`/api/AccountTransaction`);
      const result = response.data;
      const sqlTransactions = result.data || result;
      
      console.log(`✅ [CARİ RAPORU] SQL'den ${sqlTransactions.length} cari hareketi yüklendi`);
      
      // ✅ İlk kaydın tarih bilgisini kontrol et
      if (sqlTransactions.length > 0) {
        console.log('🔍 [CARİ RAPORU] İlk kayıt örneği:', {
          id: sqlTransactions[0].id,
          date: sqlTransactions[0].date,
          createdAt: sqlTransactions[0].createdAt,
          transactionType: sqlTransactions[0].transactionType
        });
      }
      
      if (!sqlTransactions || sqlTransactions.length === 0) {
        setAccountTransactions([]);
        setLoading(false);
        return;
      }
      
      // SQL verilerini frontend formatına çevir ve filtrele
      const loadedTransactions: AccountTransaction[] = sqlTransactions
        .filter((transaction: any) => {
          // Bu ekrana ait olmalı
          const matchesScreen = transaction.screenId?.toString() === screenId?.toString();
          
          console.log(`🔍 [CARİ RAPORU] İşlem ${transaction.id}: screenId=${transaction.screenId}, matchesScreen=${matchesScreen}`);
          
          return matchesScreen;
        })
        .map((transaction: any) => ({
          id: transaction.id.toString(),
          accountId: transaction.accountId?.toString() || '',
          accountName: transaction.accountName || t('accountReportTab.common.unknownAccount'),
          transactionType: transaction.transactionType || '',
          operationType: transaction.operationType,
          description: transaction.description || '',
          amount: parseFloat(transaction.amount) || 0,
          date: transaction.date || transaction.createdAt,
          createdAt: transaction.createdAt || transaction.date,
          referenceId: transaction.referenceId,
          referenceType: transaction.referenceType,
          currency: transaction.currency || 'TRY',
          paymentMethod: transaction.paymentMethod,
          screenId: transaction.screenId?.toString(),
          debitAmount: parseFloat(transaction.debitAmount) || 0,
          creditAmount: parseFloat(transaction.creditAmount) || 0,
          previousBalance: parseFloat(transaction.previousBalance) || 0,
          nextBalance: parseFloat(transaction.nextBalance) || 0,
          createdBy: transaction.createdBy || t('accountReportTab.common.unknown')
        }))
        .sort((a: AccountTransaction, b: AccountTransaction) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      
      console.log(`✅ [CARİ RAPORU] ${loadedTransactions.length} hareket (ekran ID: ${screenId}) işlendi ve sıralandı`);
      setAccountTransactions(loadedTransactions);
    } catch (err) {
      console.error("❌ [CARİ RAPORU] Cari hareketleri çekilirken hata:", err);
      setError(t('accountReportTab.errors.loadFailed'));
      setAccountTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, screenId]);

  useEffect(() => {
    fetchAccountTransactions();
  }, [fetchAccountTransactions]);

  const uniqueCustomerNames = useMemo(() => {
    const names = new Set(accountTransactions.map(item => item.accountName));
    return Array.from(names).sort();
  }, [accountTransactions]);

  useEffect(() => {
    let newFiltered = [...accountTransactions];
    if (dateFilter) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let filterStartDate: Date;
      switch (dateFilter) {
        case 'today':
          filterStartDate = today;
          const endOfToday = new Date(today); endOfToday.setHours(23, 59, 59, 999);
          newFiltered = newFiltered.filter(t => {
            const dateValue = t.createdAt || t.date;
            if (!dateValue) return false;
            const transactionDate = new Date(dateValue); // ✅ Backend'den UTC+3 geliyor
            return isValid(transactionDate) && transactionDate >= filterStartDate && transactionDate <= endOfToday;
          });
          break;
        case 'week':
          filterStartDate = new Date(today); filterStartDate.setDate(today.getDate() - 7);
          newFiltered = newFiltered.filter(t => {
            const dateValue = t.createdAt || t.date;
            if (!dateValue) return false;
            const transactionDate = new Date(dateValue); // ✅ Backend'den UTC+3 geliyor
            return isValid(transactionDate) && transactionDate >= filterStartDate;
          });
          break;
        case 'month':
          filterStartDate = new Date(today); filterStartDate.setMonth(today.getMonth() - 1);
          newFiltered = newFiltered.filter(t => {
            const dateValue = t.createdAt || t.date;
            if (!dateValue) return false;
            const transactionDate = new Date(dateValue); // ✅ Backend'den UTC+3 geliyor
            return isValid(transactionDate) && transactionDate >= filterStartDate;
          });
          break;
      }
    } else if (startDate || endDate) {
      const sDate = startDate ? new Date(new Date(startDate).setHours(0,0,0,0)) : null;
      const eDate = endDate ? new Date(new Date(endDate).setHours(23,59,59,999)): null;
      newFiltered = newFiltered.filter(t => {
        const dateValue = t.createdAt || t.date;
        if (!dateValue) return false;
        const transactionDate = new Date(dateValue); // ✅ Backend'den UTC+3 geliyor
        if (!isValid(transactionDate)) return false;

        const afterS = sDate ? transactionDate >= sDate : true;
        const beforeE = eDate ? transactionDate <= eDate : true;
        return afterS && beforeE;
      });
    }
    if (customerNameFilter.trim() !== '') {
      newFiltered = newFiltered.filter(t =>
        t.accountName.toLowerCase().includes(customerNameFilter.trim().toLowerCase())
      );
    }

    if (selectedCashier) {
      newFiltered = newFiltered.filter(t => t.createdBy === selectedCashier);
    }

    setFilteredTransactions(newFiltered);
    setPage(0);
  }, [accountTransactions, dateFilter, startDate, endDate, customerNameFilter, selectedCashier]);

  const totalFilteredSalesToCustomers = useMemo(() => {
    // Sadece QUICK_SALE, COLLECTION ve GELİR tipli işlemleri topla
    return filteredTransactions.reduce((sum, item) => {
      const typeInfo = getTransactionTypeInfo(item, t);
      // Hızlı Satış veya Tahsilat/Gelir ise topla
      if (item.transactionType === 'QUICK_SALE' || 
          item.transactionType === 'COLLECTION' || 
          typeInfo.color === 'success' || 
          typeInfo.color === 'info') {
        return sum + (item.amount || 0);
      }
      return sum;
    }, 0);
  }, [filteredTransactions]);
  
  const distinctCustomersCount = useMemo(() => {
    const customerIds = new Set(filteredTransactions.map(t => t.accountId));
    return customerIds.size;
  }, [filteredTransactions]);

  const uniqueCashiers = useMemo(() => {
    if (accountTransactions.length === 0) return [];
    const cashierSet = new Set(accountTransactions.map((transaction) => transaction.createdBy || t('accountReportTab.common.unknown')));
    return Array.from(cashierSet).sort();
  }, [accountTransactions, t]);

  // Tablo için genel cari tutar toplamı
  const overallAccountTotalAmount = useMemo(() => {
    return filteredTransactions.reduce((sum, transaction) => {
      const typeInfo = getTransactionTypeInfo(transaction, t);
      // Hızlı Satış, COLLECTION veya Tahsilat/Gelir ise pozitif ekle
      if (transaction.transactionType === 'QUICK_SALE' || 
          transaction.transactionType === 'COLLECTION' || 
          typeInfo.color === 'success' || 
          typeInfo.color === 'info') {
        return sum + (transaction.amount || 0);
      } else if (typeInfo.color === 'error') {
        // Ödeme/Gider ise negatif ekle
        return sum - (transaction.amount || 0); 
      } else {
        // Diğer durumlar için pozitif ekle
        return sum + (transaction.amount || 0);
      }
    }, 0);
  }, [filteredTransactions]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const getDateFilterLabel = (): string => {
    if (dateFilter) {
      return ({
        today: t('accountReportTab.dateFilters.today'),
        week: t('accountReportTab.dateFilters.lastWeek'),
        month: t('accountReportTab.dateFilters.lastMonth'),
      })[dateFilter] || '';
    }
    if (startDate && endDate) {
      return t('accountReportTab.dateFilters.range', {
        start: startDate.toLocaleDateString(tr.code),
        end: endDate.toLocaleDateString(tr.code),
      });
    }
    if (startDate) return t('accountReportTab.dateFilters.startOnly', { start: startDate.toLocaleDateString(tr.code) });
    if (endDate) return t('accountReportTab.dateFilters.endOnly', { end: endDate.toLocaleDateString(tr.code) });
    return '';
  };

  // Excel indirme fonksiyonu
  const exportToExcel = () => {
    if (filteredTransactions.length === 0) return;
    
    // Excel verilerini hazırla
    const excelData = filteredTransactions.map(transaction => {
      const typeInfo = getTransactionTypeInfo(transaction, t);
      
      return {
        [t('accountReportTab.table.dateTime')]: formatDateTime(transaction.createdAt),
        [t('accountReportTab.table.accountName')]: transaction.accountName || '',
        [t('accountReportTab.table.transactionType')]: typeInfo.label,
        [t('accountReportTab.table.description')]: transaction.description || '',
        [t('accountReportTab.table.amount')]: `${(transaction.transactionType === 'QUICK_SALE' || (typeInfo.color === 'success' && transaction.amount > 0)) ? '+' : 
                 ((typeInfo.color === 'error' && transaction.amount > 0) ? '-' : (transaction.amount < 0 ? '' : '+'))}${formatCurrency(transaction.amount, transaction.currency)}`,
        [t('accountReportTab.table.cashier')]: transaction.createdBy || ''
      };
    });

    // Excel çalışma kitabı oluştur
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 20 }, // Tarih/Saat
      { wch: 30 }, // Cari Adı
      { wch: 15 }, // İşlem Tipi
      { wch: 40 }, // Açıklama
      { wch: 15 }, // Tutar
      { wch: 15 }  // Kasiyer
    ];
    ws['!cols'] = colWidths;

    // Çalışma sayfasını kitaba ekle
    utils.book_append_sheet(wb, ws, t('accountReportTab.title'));

    // Dosya adını hazırla
    const fileName = `Account_Report_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

    // Excel dosyasını indir
    writeFile(wb, fileName);
  };

  if (!companyId) return <Alert severity="warning">{t('accountReportTab.errors.companyIdMissing')}</Alert>;
  if (!screenId) return <Alert severity="warning">{t('accountReportTab.errors.screenIdMissing')}</Alert>; // screenId artık zorunlu

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mr: 1 }}>{reportTitle}</Typography>
            {filteredTransactions.length > 0 && (
              <>
                <Chip
                  label={`${t('accountReportTab.summary.total')}: ${formatCurrency(totalFilteredSalesToCustomers)}`}
                  color="success"
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '4px 8px', mr: 1, mb: 0.5 }}
                />
                <Chip
                  label={t('accountReportTab.summary.distinctAccountCount', { count: distinctCustomersCount })}
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1, mb: 0.5 }}
                />
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexGrow: 1 }}>
            <Autocomplete
              options={uniqueCustomerNames}
              value={customerNameFilter || null}
              onChange={(event, newValue) => {
                setCustomerNameFilter(newValue || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('accountReportTab.filters.searchAccountName')}
                  size="small"
                  variant="outlined"
                  sx={{ width: { xs: '100%', sm: 200 }, mr: 1 }}
                />
              )}
              sx={{ mb: { xs: 1, sm: 0 } }}
            />
            {uniqueCashiers.length > 0 && (
              <Autocomplete
                options={uniqueCashiers}
                value={selectedCashier}
                onChange={(event, newValue) => {
                  setSelectedCashier(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('accountReportTab.filters.selectCashier')}
                    size="small"
                    variant="outlined"
                    sx={{ width: { xs: '100%', sm: 180 }, mr: 1 }}
                  />
                )}
                sx={{ mb: { xs: 1, sm: 0 } }}
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                <DatePicker
                  label={t('accountReportTab.filters.startDate')}
                  value={startDate}
                  onChange={(newValue) => { setStartDate(newValue); setDateFilter(null); }}
                  maxDate={endDate || undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: { xs: 'calc(50% - 4px)', sm: 150 } } } }}
                />
                <DatePicker
                  label={t('accountReportTab.filters.endDate')}
                  value={endDate}
                  onChange={(newValue) => { setEndDate(newValue); setDateFilter(null); }}
                  minDate={startDate || undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: { xs: 'calc(50% - 4px)', sm: 150 } } } }}
                />
              </LocalizationProvider>
              {(startDate || endDate || customerNameFilter || selectedCashier) && (
                <Tooltip title={t('accountReportTab.actions.clearAllFilters')}>
                  <IconButton onClick={() => { setStartDate(null); setEndDate(null); setDateFilter(null); setCustomerNameFilter(''); setSelectedCashier(null); }} size="small">
                    <FilterAltOffIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {dateFilter && (
                <Chip
                  icon={<FilterAltIcon />}
                  label={getDateFilterLabel()}
                  variant="filled"
                  color="primary"
                  size="small"
                  onDelete={() => setDateFilter(null)}
                  deleteIcon={<FilterAltOffIcon />}
                  sx={{ mr: 1.5 }}
                />
              )}
              <ButtonGroup size="small" variant="outlined">
                <Tooltip title={t('accountReportTab.tooltips.todayRecords')}>
                  <Button onClick={() => { setDateFilter('today'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'today' ? 'primary' : 'inherit'} variant={dateFilter === 'today' ? 'contained' : 'outlined'} startIcon={<CalendarTodayIcon />}>{t('accountReportTab.dateFilters.today')}</Button>
                </Tooltip>
                <Tooltip title={t('accountReportTab.tooltips.last7DaysRecords')}>
                  <Button onClick={() => { setDateFilter('week'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'week' ? 'primary' : 'inherit'} variant={dateFilter === 'week' ? 'contained' : 'outlined'} startIcon={<DateRangeIcon />}>{t('accountReportTab.dateFilters.lastWeek')}</Button>
                </Tooltip>
                <Tooltip title={t('accountReportTab.tooltips.last30DaysRecords')}>
                  <Button onClick={() => { setDateFilter('month'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'month' ? 'primary' : 'inherit'} variant={dateFilter === 'month' ? 'contained' : 'outlined'} startIcon={<EventNoteIcon />}>{t('accountReportTab.dateFilters.lastMonth')}</Button>
                </Tooltip>
              </ButtonGroup>
              
              {/* Excel İndirme Butonu */}
              <Button
                variant="contained"
                color="info"
                startIcon={<DownloadIcon />}
                onClick={exportToExcel}
                disabled={filteredTransactions.length === 0}
                sx={{ ml: 2 }}
              >
                {t('accountReportTab.actions.downloadExcel')}
              </Button>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      {loading && <Box sx={{display:'flex',justifyContent:'center',py:4}}><CircularProgress/></Box>}
      {error && <Alert severity="error" sx={{mb:2}}>{error}</Alert>}
      {!loading && !error && filteredTransactions.length === 0 &&
        <Typography variant="body1" align="center" sx={{py:4}}>
          {dateFilter||startDate||endDate||customerNameFilter
            ? t('accountReportTab.emptyState.noRecordsForCriteria')
            : t('accountReportTab.emptyState.noRecordsForScreen')}
        </Typography>
      }
      {!loading && !error && filteredTransactions.length > 0 && (
        <>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('accountReportTab.table.dateTime')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '20%' }}>{t('accountReportTab.table.accountName')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('accountReportTab.table.transactionType')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '25%' }}>{t('accountReportTab.table.description')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '10%', textAlign: 'right' }}>{t('accountReportTab.table.amount')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '10%' }}>{t('accountReportTab.table.cashier')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((transaction) => {
                  const typeInfo = getTransactionTypeInfo(transaction, t);
                  let amountColor = 'inherit';
                  if (transaction.transactionType === 'QUICK_SALE' || typeInfo.color === 'success') {
                    amountColor = 'success.main';
                  } else if (typeInfo.color === 'error') {
                    amountColor = 'error.main';
                  } else if (transaction.amount < 0) {
                    amountColor = 'error.main';
                  } else if (transaction.amount > 0) {
                    amountColor = 'success.main';
                  }

                  return (
                    <TableRow
                      key={transaction.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        backgroundColor: typeInfo.color === 'success' ? 'rgba(237, 247, 237, 0.4)' : typeInfo.color === 'error' ? 'rgba(253, 237, 237, 0.4)' : undefined
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.875rem' }}>{formatDateTime(transaction.createdAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.accountName}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        <Chip
                          icon={typeInfo.icon}
                          label={typeInfo.label}
                          size="small"
                          color={typeInfo.color}
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.description}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', fontWeight: 'medium', color: amountColor, textAlign: 'right' }}>
                        {(transaction.transactionType === 'QUICK_SALE' || (typeInfo.color === 'success' && transaction.amount > 0)) ? '+' : 
                         ((typeInfo.color === 'error' && transaction.amount > 0) ? '-' : (transaction.amount < 0 ? '' : '+'))}
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.createdBy}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ '& td': { fontWeight: 'bold', fontSize: '0.9rem' } }}>
                  <TableCell colSpan={4} sx={{ textAlign: 'right' }}>{t('accountReportTab.summary.grandTotal')}</TableCell>
                  <TableCell sx={{ textAlign: 'right', color: overallAccountTotalAmount >= 0 ? 'success.main' : 'error.main' }}>
                    {formatCurrency(overallAccountTotalAmount)}
                  </TableCell>
                  <TableCell></TableCell> {/* Kasiyer sütunu için boş */}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredTransactions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('accountReportTab.pagination.rowsPerPage')}
            labelDisplayedRows={({from,to,count}) => t('accountReportTab.pagination.displayedRows', { count, from, to })}
            rowsPerPageOptions={[5,10,25,50,100]}
          />
        </>
      )}
    </Paper>
  );
};

export default AccountReportTab; 